import { Box, Checkbox, Flex, Text, VStack } from '@chakra-ui/react'
import { EncounterSection, FieldMapValue, isEncounterSectionComplete } from '@hb/shared'
import { RefObject, useCallback, useDeferredValue, useMemo } from 'react'
import { useFormState } from 'react-final-form'

export const getSectionName = (section: EncounterSection): string => {
  switch (section.type) {
    case 'field':
      return section.field.name
    case 'review-of-systems':
      return 'Review of Systems'
    case 'notes':
      return 'Notes'
    case 'vitals':
      return 'Vitals'
    case 'risk-assessment':
      return 'Risk Assessment'
    case 'clinical-rationale':
      return 'Clinical Rationale'
    // case 'pregnancy-header':
    //   return 'Pregnancy Info'
    case 'reason-for-visit':
      return 'Reason for Visit'
    case 'allergies':
      return 'Allergies'
    case 'medications-supplements':
      return 'Medications & Supplements'
    case 'labs-diagnostics':
      return 'Labs & Diagnostics'
    case 'visit-location':
      return 'Visit Location'
    case 'first-ob-visit':
      return 'First OB Visit'
    case 'soap-note':
      return 'SOAP Note'
    case 'general-illness-exam':
      return 'General Illness Exam'
    case 'remote-assessment':
      return 'Remote Assessment'
    case 'assessment-plan':
      return 'Assessment & Plan'
    case 'icd10-selection':
      return 'ICD-10 Selection'
    case 'birthing-center-admission':
      return 'Birthing Center Admission'
    case 'mdm':
      return 'Medical Decision Making'
    case 'physical-exam':
      return 'Physical Exam'
    case 'pelvic-exam':
      return 'Pelvic Exam'
    case 'pre-visit-contact':
      return 'Pre-Visit Contact'
    case 'prenatal-exam':
      return 'Prenatal Visit'
    case 'ultrasound-header':
      return 'Report Header'
    case 'ultrasound-findings':
      return 'Findings'
    case 'dv-ipv-screening':
      return 'DV/IPV Screening'
    case 'depression-screening':
      return 'Depression & Anxiety Screening'
    case 'substance-use-counseling':
      return 'Substance Use & Tobacco Counseling'
    case 'patient-education':
      return 'Patient Education Checklist'
  }
}

// Left-hand section navigator — one row per rendered section, a read-only checkbox showing
// data completeness, and click-to-scroll. Sections tag their wrapper with
// data-section-nav-index={section.index} so rows can locate their DOM target.
export const EncounterSectionNav = ({
  sections,
  scrollContainerRef,
}: {
  sections: (EncounterSection & { index: number })[]
  scrollContainerRef: RefObject<HTMLDivElement | null>
}) => {
  const { values } = useFormState({ subscription: { values: true } })
  // Completeness walks every section's field map — defer so it never competes with keystrokes
  // (same pattern as EncounterErrorsProvider).
  const deferredValues = useDeferredValue(values)
  const data = useMemo(() => (deferredValues?.data ?? {}) as FieldMapValue, [deferredValues])
  const completeness = useMemo(
    () => sections.map(section => isEncounterSectionComplete(section, data)),
    [sections, data],
  )

  const scrollToSection = useCallback(
    (index: number) => {
      const el = scrollContainerRef.current?.querySelector<HTMLElement>(
        `[data-section-nav-index="${index}"]`,
      )
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [scrollContainerRef],
  )

  if (!sections.length) return null

  return (
    <Box
      display={{ base: 'none', lg: 'block' }}
      w="230px"
      flexShrink={0}
      h="100%"
      overflowY="auto"
      borderRightWidth={1}
      borderColor="gray.300"
      _dark={{ borderColor: 'gray.700', bg: 'gray.800' }}
      bg="gray.50"
      py={3}
      px={2}>
      <VStack align="stretch" spacing={0}>
        {sections.map((section, i) => (
          <Flex
            // Checkbox renders an <input>, so the row is a clickable Flex rather than a <button>
            // (nesting an input inside a button is invalid HTML).
            key={`${section.type}-${section.index}`}
            role="button"
            align="center"
            gap={2}
            px={2}
            py={1.5}
            borderRadius={6}
            cursor="pointer"
            _hover={{ bg: 'blackAlpha.100', _dark: { bg: 'whiteAlpha.200' } }}
            onClick={() => scrollToSection(section.index)}>
            <Checkbox
              isChecked={completeness[i]}
              colorScheme="green"
              size="sm"
              pointerEvents="none"
              tabIndex={-1}
            />
            <Text fontSize="sm" noOfLines={1}>
              {getSectionName(section)}
            </Text>
          </Flex>
        ))}
      </VStack>
    </Box>
  )
}
